import z from 'zod';
import { AttributeSettingType, QuestionTypes, Sentiments } from './enums';
import { isoDateTimeSchema, stringSchema, uuidSchema } from './validation-utils';
/**
 * ENTITIES
 */
export const Survey = z
    .object({
    surveyId: uuidSchema,
    surveyName: z.string(),
    surveyDate: isoDateTimeSchema,
    isPublished: z.boolean(),
    previousSurveyId: uuidSchema.nullable(),
    categories: z.array(z
        .object({
        categoryId: uuidSchema,
        name: z.string(),
        questions: z.array(z
            .object({
            questionId: uuidSchema,
            text: z.string(),
            questionType: QuestionTypes,
        })
            .readonly()),
    })
        .readonly()),
})
    .readonly();
export const SurveyAttribute = z
    .object({
    id: uuidSchema,
    name: z.string(),
    type: AttributeSettingType.nullable(),
    options: z.array(z
        .object({
        id: uuidSchema,
        name: z.string(),
    })
        .readonly()),
})
    .readonly();
const permittedAttribute = z
    .object({
    attributeId: uuidSchema,
    optionIds: z.array(uuidSchema),
})
    .readonly();
/**
 * A client user's viewing permission for one survey. Holding a permission is
 * what grants the user access to the survey; `canViewComments` and `attributes`
 * narrow what they may see within it. `attributes` lists the attributes the user
 * may filter by together with the selectable options of each.
 */
export const SurveyUserPermission = z
    .object({
    userId: uuidSchema,
    canViewComments: z.boolean(),
    attributes: z.array(permittedAttribute),
})
    .readonly();
/**
 * One survey the current user may view, and what they may see within it. Used
 * by the client screens to narrow survey pickers and attribute filters to what
 * the caller is allowed to select.
 */
export const SurveyViewingPermission = z
    .object({
    surveyId: uuidSchema,
    canViewComments: z.boolean(),
    attributes: z.array(permittedAttribute),
})
    .readonly();
// Lightweight tag reference attached to an analysed comment (id + display name).
export const CommentTagRef = z
    .object({
    id: uuidSchema,
    name: z.string(),
})
    .readonly();
const surveyCommentFields = z.object({
    answerId: uuidSchema,
    questionId: uuidSchema,
    answer: z.string(),
    sentiment: Sentiments,
    tags: z.array(CommentTagRef),
});
// A single analysed free-text comment (post PII-masking + sentiment/tagging).
export const SurveyComment = surveyCommentFields.readonly();
export const CommentTagGroup = z
    .object({
    id: uuidSchema,
    name: z.string(),
    description: z.string(),
})
    .readonly();
export const CommentTag = z
    .object({
    id: uuidSchema,
    groupId: uuidSchema,
    name: z.string(),
    description: z.string(),
})
    .readonly();
// Tags available for filtering a survey's comments, grouped by tag group.
export const CommentTagCatalogue = z
    .object({
    groups: z.array(CommentTagGroup),
    tags: z.array(CommentTag),
})
    .readonly();
export const CategoryMapping = z
    .object({
    currentCategoryId: uuidSchema,
    previousCategoryId: uuidSchema.nullable(),
})
    .readonly();
export const QuestionMapping = z
    .object({
    currentQuestionId: uuidSchema,
    previousQuestionId: uuidSchema.nullable(),
})
    .readonly();
export const AttributeSettingMapping = z
    .object({
    currentAttributeId: uuidSchema,
    previousAttributeId: uuidSchema.nullable(),
})
    .readonly();
export const AttributeOptionMapping = z
    .object({
    currentOptionId: uuidSchema,
    previousOptionId: uuidSchema.nullable(),
})
    .readonly();
export const ScoreDistribution = z
    .object({
    favorable: z.number(),
    neutral: z.number(),
    unfavorable: z.number(),
})
    .readonly();
export const SurveyAttributeOptionScores = z
    .object({
    id: uuidSchema,
    previousId: uuidSchema.nullable(),
    settingId: uuidSchema,
    name: z.string(),
    di: z.number().nullable(),
    deviation: z.number().nullable(),
})
    .readonly();
const categoryHighlight = z
    .object({
    id: uuidSchema,
    name: z.string(),
    di: z.number().nullable(),
    deviation: z.number().nullable(),
})
    .readonly();
export const SurveyOverallScores = z
    .object({
    surveyId: uuidSchema,
    surveyDate: isoDateTimeSchema,
    di: z.number().nullable(),
    deviation: z.number().nullable(),
    responseRate: z
        .object({
        responders: z.number(),
        total: z.number(),
    })
        .readonly(),
    highestCategory: categoryHighlight.nullable(),
    mostImprovedCategory: categoryHighlight.nullable(),
})
    .readonly();
export const SurveyScoresByAttributeOption = z
    .object({
    surveyId: uuidSchema,
    scores: z.array(SurveyAttributeOptionScores),
})
    .readonly();
const categoryScore = z
    .object({
    id: uuidSchema,
    previousCategoryId: uuidSchema.nullable(),
    name: z.string(),
    di: z.number().nullable(),
    deviation: z.number().nullable(),
})
    .readonly();
export const SurveyScoresByCategory = z
    .object({
    surveyId: uuidSchema,
    surveyDate: isoDateTimeSchema,
    categories: z.array(categoryScore),
})
    .readonly();
const attributeProduct = z.array(z.object({ id: uuidSchema, name: z.string() }));
export const SurveyScoresByCategoryXAttributes = z
    .object({
    surveyId: uuidSchema,
    surveyDate: isoDateTimeSchema,
    all: z
        .object({
        responderCount: z.number(),
        categories: z.array(categoryScore),
    })
        .readonly(),
    categories: z.array(z
        .object({
        id: uuidSchema,
        previousCategoryId: uuidSchema.nullable(),
        name: z.string(),
        questionCount: z.number(),
        responderCount: z.number(),
        di: z.number().nullable(),
        deviation: z.number().nullable(),
        attributes: z
            .array(z
            .object({
            product: attributeProduct,
            responderCount: z.number(),
            di: z.number().nullable(),
            deviation: z.number().nullable(),
            distribution: ScoreDistribution.nullable(),
        })
            .readonly())
            .nullable(),
        distribution: ScoreDistribution.nullable(),
    })
        .readonly()),
})
    .readonly();
export const SurveyScoresByQuestion = z
    .object({
    surveyId: uuidSchema,
    surveyDate: isoDateTimeSchema,
    questions: z.array(z
        .object({
        id: uuidSchema,
        categoryId: uuidSchema,
        name: z.string(),
        text: z.string(),
        di: z.number().nullable(),
        deviation: z.number().nullable(),
    })
        .readonly()),
})
    .readonly();
const highLowOption = z
    .object({
    name: z.string(),
    /** Percentage (0..100) of the option's select-typed answers that are favorable (Likert 1-2). */
    favorableRate: z.number(),
})
    .readonly();
export const SurveyHighLowAnalysisScores = z
    .object({
    surveyId: uuidSchema,
    settingId: uuidSchema,
    /** `null` when fewer than 2 options in the setting have a computable favorable rate. */
    scores: z
        .object({
        highestOption: highLowOption,
        lowestOption: highLowOption,
        questions: z
            .array(z
            .object({
            id: uuidSchema,
            text: z.string(),
            favorableRateForHighest: z.number(),
            favorableRateForLowest: z.number(),
            delta: z.number(),
        })
            .readonly())
            .readonly(),
    })
        .readonly()
        .nullable(),
})
    .readonly();
export const SurveyScoresByQuestionXAttributes = z
    .object({
    surveyId: uuidSchema,
    surveyDate: isoDateTimeSchema,
    categories: z.array(z
        .object({
        id: uuidSchema,
        name: z.string(),
        questions: z.array(z
            .object({
            id: uuidSchema,
            previousQuestionId: uuidSchema.nullable(),
            name: z.string(),
            text: z.string(),
            categoryId: uuidSchema,
            categoryName: z.string(),
            questionType: QuestionTypes,
            di: z.number().nullable(),
            deviation: z.number().nullable(),
            responseRate: z.number().nullable(),
            attributes: z
                .array(z
                .object({
                product: attributeProduct,
                di: z.number().nullable(),
                deviation: z.number().nullable(),
                distribution: ScoreDistribution.nullable(),
            })
                .readonly())
                .nullable(),
            distribution: ScoreDistribution.nullable(),
        })
            .readonly()),
    })
        .readonly()),
})
    .readonly();
/**
 * REQUEST VALIDATION SCHEMAS
 */
export const GetSurveyDataRequestParams = z.object({
    surveyId: uuidSchema,
});
export const GetSurveysDataRequestQuery = z.object({
    surveyIds: stringSchema.transform((value) => JSON.parse(value)).pipe(z.array(uuidSchema)),
});
export const GetScoresByAttributesRequestQuery = z.object({
    surveyIds: stringSchema.transform((value) => JSON.parse(value)).pipe(z.array(uuidSchema).min(1)),
    settingId: uuidSchema,
});
export const GetScoresByCategoryXAttributesQuery = z.object({
    attributes: stringSchema
        .transform((value) => JSON.parse(value))
        .pipe(z.array(z.object({
        attributeId: uuidSchema,
        optionIds: z.array(uuidSchema),
    }))),
});
export const GetSurveyMappingsRequestParams = z.object({
    surveyId: uuidSchema,
});
export const MapSurveyCategoryRequestParams = z.object({
    organizationId: uuidSchema,
});
export const MapSurveyCategoryRequestBody = z.object({
    currentSurveyId: uuidSchema,
    previousSurveyId: uuidSchema,
    mappings: z.array(z.object({
        currentCategoryId: uuidSchema,
        previousCategoryId: uuidSchema.nullable().or(z.literal('').transform(() => null)),
    })),
});
export const MapSurveyQuestionRequestParams = z.object({
    organizationId: uuidSchema,
});
export const MapSurveyQuestionRequestBody = z.object({
    currentSurveyId: uuidSchema,
    previousSurveyId: uuidSchema,
    mappings: z.array(z.object({
        currentQuestionId: uuidSchema,
        previousQuestionId: uuidSchema.nullable().or(z.literal('').transform(() => null)),
    })),
});
export const SURVEY_COMMENT_MAX_LENGTH = 5000;
/**
 * A consultant's correction of one analysed comment. All three fields are
 * replaced together: the request carries the comment's full state after the
 * edit, not a partial patch.
 */
export const UpdateSurveyCommentRequestBody = z.object({
    answer: stringSchema.max(SURVEY_COMMENT_MAX_LENGTH),
    sentiment: Sentiments,
    tagIds: z.array(uuidSchema),
});
export const UpdateSurveyCommentRequestParams = z.object({
    organizationId: uuidSchema,
    surveyId: uuidSchema,
    answerId: uuidSchema,
    questionId: uuidSchema,
});
export const SURVEY_COMMENT_SEARCH_MAX_LENGTH = 256;
export const GetSurveyCommentsRequestQuery = z.object({
    questionIds: stringSchema
        .transform((value) => JSON.parse(value))
        .pipe(z.array(uuidSchema))
        .optional(),
    sentiment: Sentiments.optional(),
    // Flat list of selected tag ids. The backend groups them by tag group to
    // apply the AND-of-ORs matching rule (same-group OR, cross-group AND).
    tagIds: stringSchema
        .transform((value) => JSON.parse(value))
        .pipe(z.array(uuidSchema))
        .optional(),
    // Substring search against the masked answer text.
    search: stringSchema.max(SURVEY_COMMENT_SEARCH_MAX_LENGTH).optional(),
    page: stringSchema
        .transform((value) => Number.parseInt(value, 10))
        .pipe(z.int().min(1))
        .optional(),
    pageSize: stringSchema
        .transform((value) => Number.parseInt(value, 10))
        .pipe(z.int().min(1).max(200))
        .optional(),
});
export const MapSurveyAttributeSettingRequestParams = z.object({
    organizationId: uuidSchema,
});
export const MapSurveyAttributeSettingRequestBody = z.object({
    currentSurveyId: uuidSchema,
    previousSurveyId: uuidSchema,
    mappings: z.array(z.object({
        currentAttributeId: uuidSchema,
        previousAttributeId: uuidSchema.nullable().or(z.literal('').transform(() => null)),
    })),
});
export const SaveAttributeSettingTypesRequestParams = z.object({
    organizationId: uuidSchema,
    surveyId: uuidSchema,
});
export const SaveAttributeSettingTypesRequestBody = z.object({
    types: z.array(z.object({
        settingId: uuidSchema,
        type: AttributeSettingType,
    })),
});
export const MapSurveyAttributeOptionRequestParams = z.object({
    organizationId: uuidSchema,
});
export const MapSurveyAttributeOptionRequestBody = z.object({
    currentSurveyId: uuidSchema,
    previousSurveyId: uuidSchema,
    mappings: z.array(z.object({
        currentOptionId: uuidSchema,
        previousOptionId: uuidSchema.nullable().or(z.literal('').transform(() => null)),
    })),
});
export const GetSurveyQuestionCorrelationsRequestParams = z.object({
    organizationId: uuidSchema,
    surveyId: uuidSchema,
    questionId: uuidSchema,
});
export const GetSurveyHighLowAnalysisScoresRequestParams = z.object({
    organizationId: uuidSchema,
    surveyId: uuidSchema,
});
export const GetSurveyHighLowAnalysisScoresRequestQuery = z.object({
    settingId: uuidSchema,
    topN: stringSchema
        .transform((value) => Number.parseInt(value, 10))
        .pipe(z.int().min(1))
        .optional(),
});
export const PublishSurveyRequestParams = z.object({
    organizationId: uuidSchema,
    surveyId: uuidSchema,
});
export const PublishSurveyRequestBody = z.object({
    isPublished: z.boolean(),
});
export const GetSurveyUserPermissionListRequestParams = z.object({
    organizationId: uuidSchema,
    surveyId: uuidSchema,
});
export const SurveyUserPermissionRequestParams = z.object({
    organizationId: uuidSchema,
    surveyId: uuidSchema,
    userId: uuidSchema,
});
// Client-side management paths take the organization from the caller's token,
// so only the user (and survey) are addressed.
export const UserSurveyPermissionListRequestParams = z.object({
    userId: uuidSchema,
});
export const UserSurveyPermissionRequestParams = z.object({
    userId: uuidSchema,
    surveyId: uuidSchema,
});
export const SaveSurveyUserPermissionRequestBody = z.object({
    canViewComments: z.boolean(),
    attributes: z.array(z.object({
        attributeId: uuidSchema,
        optionIds: z.array(uuidSchema),
    })),
});
export const LegacyRawdataType = z.enum(['current_system', 'taro']);
export const ImportLegacyRawdataRequestParams = z.object({
    organizationId: uuidSchema.min(1),
});
/**
 * RESPONSE TYPES
 */
export const GetSurveysResponse = z.object({ surveys: z.array(Survey) }).readonly();
/**
 * Lightweight survey-project list for pickers (e.g. selecting the survey an
 * overwrite import targets). `surveyProjectId` is the value stored on the task;
 * `name` is the survey's display name.
 */
export const GetSurveyProjectsResponse = z
    .object({
    surveyProjects: z.array(z
        .object({
        surveyProjectId: uuidSchema,
        name: z.string(),
    })
        .readonly()),
})
    .readonly();
export const GetSurveyMappingDataResponse = z
    .object({
    surveyId: uuidSchema,
    categoryMappings: z.array(CategoryMapping),
    questionMappings: z.array(QuestionMapping),
    attributeSettingMappings: z.array(AttributeSettingMapping),
    attributeOptionMappings: z.array(AttributeOptionMapping),
})
    .readonly();
export const GetScoresResponse = z.object({ scores: z.array(SurveyOverallScores) }).readonly();
export const GetScoresByCategoryResponse = z.object({ scores: z.array(SurveyScoresByCategory) }).readonly();
export const GetScoresByQuestionResponse = z.object({ scores: z.array(SurveyScoresByQuestion) }).readonly();
export const GetSurveyAttributesResponse = z.object({ attributes: z.array(SurveyAttribute) }).readonly();
export const GetSurveysAttributesResponse = z.array(z
    .object({
    surveyId: uuidSchema,
    attributes: z.array(SurveyAttribute),
})
    .readonly());
export const GetSurveyScoresByCategoryXAttributesResponse = SurveyScoresByCategoryXAttributes;
export const GetSurveyScoresByQuestionXAttributesResponse = SurveyScoresByQuestionXAttributes;
export const GetSurveysScoresByAttributesResponse = z
    .object({ surveys: z.array(SurveyScoresByAttributeOption) })
    .readonly();
export const GetSurveyHighLowAnalysisScoresResponse = SurveyHighLowAnalysisScores;
export const GetSurveyCommentsResponse = z
    .object({
    surveyId: uuidSchema,
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
    comments: z.array(SurveyComment),
})
    .readonly();
/**
 * A comment as the consultant screens see it: the analysed result plus the
 * responder's text before masking. Deliberately separate from
 * `GetSurveyCommentsResponse` so the unmasked text cannot reach client users.
 */
export const SystemSurveyComment = surveyCommentFields.extend({ rawAnswer: z.string() }).readonly();
export const GetSystemSurveyCommentsResponse = z
    .object({
    surveyId: uuidSchema,
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
    comments: z.array(SystemSurveyComment),
})
    .readonly();
export const UpdateSurveyCommentResponse = z.object({ comment: SystemSurveyComment }).readonly();
export const GetSurveyCommentQuestionsResponse = z
    .object({
    surveyId: uuidSchema,
    questions: z.array(z
        .object({
        id: uuidSchema,
        text: z.string(),
    })
        .readonly()),
})
    .readonly();
export const GetSurveyQuestionCorrelationsResponse = z
    .object({
    surveyId: uuidSchema,
    questionId: uuidSchema,
    correlations: z
        .array(z
        .object({
        questionId: uuidSchema,
        questionText: z.string(),
        categoryId: uuidSchema,
        coefficient: z.number(),
    })
        .readonly())
        .readonly(),
})
    .readonly();
export const GetCommentTagsResponse = CommentTagCatalogue;
/**
 * The current user's own viewing permissions. `unrestricted` marks a user who is
 * never narrowed by them (an admin), in which case `permissions` is empty.
 */
export const GetSurveyPermissionsResponse = z
    .object({
    unrestricted: z.boolean(),
    permissions: z.array(SurveyViewingPermission),
})
    .readonly();
/**
 * One user's viewing permissions, as read by an administrator. `unrestricted`
 * marks a user the per-survey permissions never narrow (an admin), in which case
 * `permissions` is empty.
 */
export const GetUserSurveyPermissionsResponse = z
    .object({
    userId: uuidSchema,
    unrestricted: z.boolean(),
    permissions: z.array(SurveyViewingPermission),
})
    .readonly();
export const GetSurveyUserPermissionListResponse = z
    .object({
    surveyId: uuidSchema,
    permissions: z.array(SurveyUserPermission),
})
    .readonly();
export const SaveSurveyUserPermissionResponse = z
    .object({
    surveyId: uuidSchema,
    permission: SurveyUserPermission,
})
    .readonly();
export const LegacyRawdataImportResponse = z
    .object({
    surveyId: uuidSchema,
    projectId: uuidSchema,
    categoriesCount: z.number(),
    questionsCount: z.number(),
    answersCount: z.number(),
})
    .readonly();
/**
 * DI or deviation scores can be `null` for a number of reasons,
 * most notably a lack of responses from a group of participants.
 * This function returns a type guard that filters out such objects.
 *
 * @param key - The key of the property to check.
 * @returns A type guard that returns true if the property is not null, false otherwise.
 */
export const notNullScorePredicate = (key) => (item) => {
    return item[key] !== null;
};
//# sourceMappingURL=survey.js.map